import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Plus, Save, X, Trash2, Edit3, Sparkles, Star, BookOpen, Loader2, Search, Eye, EyeOff } from 'lucide-react';
import AiGenerateButton from '@/components/ai/AiGenerateButton';

const CATEGORIES = ['Mindset', 'Mental Health', 'Purpose', 'Growth', 'Leadership', 'Habits', 'Healing'];

const EMPTY = {
  title: '', author: '', category: 'Mindset', isbn: '', cover_image: '',
  affiliate_link: '', why_mickey_recommends: '', mickey_notes: '',
  tags: [], is_mickeys_own: false, is_published: true, display_order: 999,
};

export default function BookManager() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('All');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [tagsInput, setTagsInput] = useState('');
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedN, setSeedN] = useState(15);
  const [seedReplace, setSeedReplace] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');
  const [titleAiOpen, setTitleAiOpen] = useState(false);
  const [titleAiInput, setTitleAiInput] = useState('');
  const [titleAiAuthor, setTitleAiAuthor] = useState('');
  const [titleAiBusy, setTitleAiBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/books');
      setBooks(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = books.filter((b) => {
    if (filterCat !== 'All' && b.category !== filterCat && !(filterCat === "Mickey's Own" && b.is_mickeys_own)) return false;
    if (q && !((b.title || '') + ' ' + (b.author || '')).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const startEdit = (b) => {
    setEditing(b);
    setTagsInput((b.tags || []).join(', '));
    setErr('');
  };
  const startCreate = () => {
    setEditing({ ...EMPTY, id: null });
    setTagsInput('');
    setErr('');
  };
  const closeEditor = () => { setEditing(null); setTagsInput(''); setErr(''); };

  const save = async () => {
    if (!editing.title?.trim()) { setErr('Title is required.'); return; }
    const payload = { ...editing, tags: tagsInput.split(',').map(t => t.trim().toLowerCase()).filter(Boolean) };
    try {
      if (editing.id) {
        await api.put(`/admin/books/${editing.id}`, payload);
      } else {
        delete payload.id;
        await api.post('/admin/books', payload);
      }
      closeEditor();
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.');
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await api.delete(`/admin/books/${b.id}`);
    load();
  };

  const runSeed = async () => {
    setSeeding(true); setSeedMsg(''); setErr('');
    try {
      const r = await api.post('/admin/books/seed', { n: seedN, replace: seedReplace });
      setSeedMsg(`AI added ${r.data.inserted} new books. Total: ${r.data.total}.`);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Seed failed.');
    } finally { setSeeding(false); }
  };

  const runTitleAi = async () => {
    const title = titleAiInput.trim();
    if (!title) { setErr('Enter a book title.'); return; }
    setTitleAiBusy(true); setSeedMsg(''); setErr('');
    try {
      const r = await api.post('/admin/books/ai-by-title', { title, author: titleAiAuthor.trim() });
      if (r.data.duplicate) {
        setSeedMsg(`"${r.data.book.title}" was already on the shelf — skipped.`);
      } else {
        setSeedMsg(`Added "${r.data.book.title}" by ${r.data.book.author}.`);
      }
      setTitleAiInput(''); setTitleAiAuthor('');
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'AI lookup failed.');
    } finally {
      setTitleAiBusy(false);
    }
  };

  return (
    <div data-testid="book-manager">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Mickey’s Bookshelf</div>
          <div className="font-display uppercase text-2xl mt-1">Book Library</div>
          <div className="text-sm text-muted-kc mt-1">Curate the books Mickey recommends. Add your own, or let AI seed more from his voice.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setTitleAiOpen(!titleAiOpen); setSeedOpen(false); }} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-[color:var(--kc-gold)] text-ink hover:bg-gold-soft transition-colors text-sm font-semibold" data-testid="book-ai-title-toggle"><Sparkles size={14} className="text-[color:var(--kc-gold)]"/> AI by title</button>
          <button onClick={() => { setSeedOpen(!seedOpen); setTitleAiOpen(false); }} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-kc-strong text-ink hover:bg-surface-2 transition-colors text-sm font-semibold" data-testid="book-ai-seed-toggle"><Sparkles size={14}/> Bulk seed</button>
          <button onClick={startCreate} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="book-add-btn"><Plus size={14}/> Add book</button>
        </div>
      </div>

      {/* AI by title panel — Mickey types a title and AI looks it up + enriches in his voice */}
      {titleAiOpen && (
        <div className="mb-5 p-5 bg-gold-soft border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] flex flex-col md:flex-row md:items-end gap-3" data-testid="book-title-ai-panel">
          <div className="flex-1">
            <div className="text-sm font-semibold inline-flex items-center gap-1.5"><Sparkles size={14} className="text-[color:var(--kc-gold-2)]"/> Add one book by title</div>
            <div className="text-xs text-muted-kc mt-0.5 mb-2">Type a book title — AI fills in the author, ISBN, cover, category, tags, and writes Mickey's recommendation + notes in his voice.</div>
            <input
              value={titleAiInput}
              onChange={(e) => setTitleAiInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !titleAiBusy) runTitleAi(); }}
              placeholder="e.g. Find Your Yellow Tux"
              className="w-full h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm focus:outline-none focus:border-[color:var(--kc-gold)]"
              data-testid="book-title-ai-input"
            />
          </div>
          <div className="md:w-56">
            <div className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold mb-1">Author (optional)</div>
            <input
              value={titleAiAuthor}
              onChange={(e) => setTitleAiAuthor(e.target.value)}
              placeholder="optional hint"
              className="w-full h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm focus:outline-none focus:border-[color:var(--kc-gold)]"
              data-testid="book-title-ai-author"
            />
          </div>
          <button onClick={runTitleAi} disabled={titleAiBusy} className="h-10 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="book-title-ai-run">
            {titleAiBusy ? <><Loader2 size={14} className="animate-spin"/> Looking up…</> : <><Sparkles size={14}/> Add with AI</>}
          </button>
        </div>
      )}

      {/* AI Seed panel */}
      {seedOpen && (
        <div className="mb-5 p-5 bg-gold-soft border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] flex flex-col md:flex-row md:items-center gap-3" data-testid="book-seed-panel">
          <Sparkles className="text-[color:var(--kc-gold-2)]" size={18}/>
          <div className="flex-1">
            <div className="text-sm font-semibold">Let GPT-4o curate more books in Mickey’s voice.</div>
            <div className="text-xs text-muted-kc mt-0.5">Real books only. Distributed across all 7 categories. Skips duplicates.</div>
          </div>
          <label className="text-xs">Books to add:</label>
          <input type="number" min="5" max="50" value={seedN} onChange={(e) => setSeedN(parseInt(e.target.value) || 15)} className="kc-input-sm w-20" data-testid="book-seed-n"/>
          <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={seedReplace} onChange={(e) => setSeedReplace(e.target.checked)} data-testid="book-seed-replace"/> Replace existing</label>
          <button onClick={runSeed} disabled={seeding} className="h-9 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="book-seed-run">
            {seeding ? <><Loader2 size={12} className="animate-spin"/> Generating…</> : <><Sparkles size={12}/> Run AI</>}
          </button>
        </div>
      )}
      {seedMsg && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-[color:var(--kc-success)] rounded-[var(--kc-radius-sm)] text-sm">{seedMsg}</div>}
      {err && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-[color:var(--kc-danger)] rounded-[var(--kc-radius-sm)] text-sm">{err}</div>}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="book-filters">
        {['All', "Mickey's Own", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setFilterCat(c)} className={`px-3 h-8 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${filterCat === c ? 'bg-ink text-white' : 'bg-surface-2 text-ink-2 hover:bg-kc'}`}>{c}</button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-kc"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or author…" className="h-9 pl-9 pr-3 rounded-[var(--kc-radius-sm)] border border-kc-strong text-sm w-56" data-testid="book-search"/>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-xs uppercase tracking-wider">
            <tr><th className="p-3">Cover</th><th className="p-3">Title</th><th className="p-3">Author</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-muted-kc">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-kc">No books match.</td></tr>}
            {filtered.map((b) => (
              <tr key={b.id} className="border-t border-kc">
                <td className="p-3">
                  <div className="w-10 h-14 bg-ink rounded overflow-hidden border border-kc flex items-center justify-center">
                    {b.cover_image ? <img src={b.cover_image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }}/> : <BookOpen className="text-[color:var(--kc-gold)]" size={14}/>}
                  </div>
                </td>
                <td className="p-3">
                  <div className="font-semibold flex items-center gap-1.5">
                    {b.is_mickeys_own && <Star size={12} className="text-[color:var(--kc-gold)] fill-[color:var(--kc-gold)]"/>}
                    {b.title}
                  </div>
                  <div className="text-xs text-muted-kc mt-0.5 line-clamp-1">{b.why_mickey_recommends}</div>
                </td>
                <td className="p-3 text-muted-kc">{b.author}</td>
                <td className="p-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-surface-2">{b.category}</span></td>
                <td className="p-3">{b.is_published === false ? <span className="text-xs text-muted-kc inline-flex items-center gap-1"><EyeOff size={12}/> Hidden</span> : <span className="text-xs text-[color:var(--kc-success)] inline-flex items-center gap-1"><Eye size={12}/> Live</span>}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(b)} className="text-xs font-semibold inline-flex items-center gap-1 hover:text-[color:var(--kc-gold-2)] mr-3" data-testid={`book-edit-${b.id}`}><Edit3 size={12}/> Edit</button>
                  <button onClick={() => remove(b)} className="text-xs font-semibold inline-flex items-center gap-1 text-[color:var(--kc-danger)] hover:underline" data-testid={`book-del-${b.id}`}><Trash2 size={12}/> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-0 md:p-4" data-testid="book-editor-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeEditor} />
          <div className="relative w-full md:max-w-2xl bg-white rounded-t-2xl md:rounded-[var(--kc-radius-md)] shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-kc px-5 py-3 flex items-center justify-between z-10">
              <div className="font-display uppercase text-lg">{editing.id ? 'Edit book' : 'Add book'}</div>
              <button onClick={closeEditor} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-surface-2"><X size={16}/></button>
            </div>
            <div className="p-5 space-y-3">
              <BkField label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="bk-title"/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BkField label="Author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testid="bk-author"/>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Category</label>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="kc-input" data-testid="bk-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BkField label="ISBN (13-digit, optional — auto-fetches cover)" value={editing.isbn} onChange={(v) => setEditing({ ...editing, isbn: v })} testid="bk-isbn"/>
                <BkField label="Cover image URL" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="bk-cover"/>
              </div>
              {editing.cover_image && (
                <div className="flex items-center gap-2">
                  <img src={editing.cover_image} alt="" className="w-16 h-24 object-cover rounded border border-kc"/>
                  <span className="text-xs text-muted-kc">Cover preview</span>
                </div>
              )}
              <BkField label="Affiliate / buy link" value={editing.affiliate_link} onChange={(v) => setEditing({ ...editing, affiliate_link: v })} placeholder="https://amzn.to/..." testid="bk-affiliate"/>
              <BkField label="Why Mickey recommends (one short sentence)" value={editing.why_mickey_recommends} onChange={(v) => setEditing({ ...editing, why_mickey_recommends: v })} testid="bk-why" aiField="why_mickey_recommends" aiLength="short" extraContext={{ title: editing.title, author: editing.author, category: editing.category }}/>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-xs font-semibold uppercase tracking-wider">Mickey’s notes (longer, 2-3 paragraphs)</label>
                  <AiGenerateButton
                    page="book"
                    field="mickey_notes"
                    currentValue={editing.mickey_notes || ''}
                    onApply={(text) => setEditing({ ...editing, mickey_notes: text })}
                    length="long"
                    extraContext={{ title: editing.title, author: editing.author, category: editing.category, why_mickey_recommends: editing.why_mickey_recommends }}
                  />
                </div>
                <textarea value={editing.mickey_notes || ''} onChange={(e) => setEditing({ ...editing, mickey_notes: e.target.value })} rows={5} className="kc-input" data-testid="bk-notes"/>
              </div>
              <BkField label="Tags (comma-separated)" value={tagsInput} onChange={(v) => setTagsInput(v)} placeholder="resilience, habits, trauma" testid="bk-tags"/>
              <div className="flex items-center gap-5 pt-2 border-t border-kc">
                <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_mickeys_own} onChange={(e) => setEditing({ ...editing, is_mickeys_own: e.target.checked })} data-testid="bk-mickeys-own"/> Mickey’s own book (featured at top)</label>
                <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_published !== false} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} data-testid="bk-published"/> Published (visible on bookshelf)</label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-kc px-5 py-3 flex items-center justify-end gap-2">
              <button onClick={closeEditor} className="h-10 px-4 rounded-[var(--kc-radius-sm)] text-sm font-semibold hover:bg-surface-2">Cancel</button>
              <button onClick={save} className="h-10 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)] inline-flex items-center gap-1" data-testid="bk-save"><Save size={14}/> Save book</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .kc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.9rem; outline: none; background: white; }
        .kc-input:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }
        .kc-input-sm { padding: 0.35rem 0.5rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.85rem; outline: none; background: white; }
      `}</style>
    </div>
  );
}

const BkField = ({ label, value, onChange, placeholder = '', testid, aiField, aiLength, extraContext }) => (
  <div>
    <div className="flex items-center justify-between gap-2 mb-1">
      <label className="block text-xs font-semibold uppercase tracking-wider">{label}</label>
      {aiField && (
        <AiGenerateButton
          page="book"
          field={aiField}
          currentValue={value || ''}
          onApply={(text) => onChange(text)}
          length={aiLength || 'short'}
          extraContext={extraContext}
        />
      )}
    </div>
    <input value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="kc-input" data-testid={testid}/>
  </div>
);
